import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PermitDepartmentService } from '../../services/permit-department.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { BcitiConfirmDialogComponent } from '../../../global/components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';
import { PermitDepartment, PermitDepartmentManager, permitDepartmentErrorKey, permitDepartmentName } from '../../models/permit-department.model';

@Component({
    selector: 'app-permit-config-departments',
    templateUrl: './permit-config-departments.component.html',
    styleUrls: ['./permit-config-departments.component.scss'],
})
export class PermitConfigDepartmentsComponent implements OnInit, OnDestroy {
    @Output() edit = new EventEmitter<PermitDepartment>();

    activeDepartments: PermitDepartment[] = [];
    archivedDepartments: PermitDepartment[] = [];
    loading = true;

    private destroy$ = new Subject<void>();

    constructor(
        private departmentService: PermitDepartmentService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.loadDepartments();
    }

    /** Next implicit order value: max over ALL departments (active + archived) + 1. */
    get nextOrder(): number {
        const all = [...this.activeDepartments, ...this.archivedDepartments];
        if (!all.length) return 0;
        return Math.max(...all.map((d) => d.order || 0)) + 1;
    }

    loadDepartments(): void {
        this.loading = true;
        this.departmentService
            .getDepartments()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (departments) => {
                    const sorted = (departments || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
                    this.activeDepartments = sorted.filter((d) => d.isActive);
                    this.archivedDepartments = sorted.filter((d) => !d.isActive);
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                    this.messageService.error(this.translate.instant('permit.config.common.error'));
                },
            });
    }

    departmentName(department: PermitDepartment): string {
        return permitDepartmentName(department.name, this.translate.currentLang || 'fr');
    }

    // List responses populate managers (backend contract); raw ids are ignored for display.
    private resolvedManagers(department: PermitDepartment): PermitDepartmentManager[] {
        return (department.managers || []).filter((m): m is PermitDepartmentManager => typeof m !== 'string');
    }

    hasManagers(department: PermitDepartment): boolean {
        return this.resolvedManagers(department).length > 0;
    }

    managerInitials(department: PermitDepartment): string[] {
        return this.resolvedManagers(department)
            .slice(0, 3)
            .map((m) => `${(m.firstName || '?').charAt(0)}${(m.lastName || '').charAt(0)}`.toUpperCase());
    }

    extraManagerCount(department: PermitDepartment): number {
        const count = this.resolvedManagers(department).length;
        return count > 3 ? count - 3 : 0;
    }

    onEdit(department: PermitDepartment): void {
        this.edit.emit(department);
    }

    confirmArchive(department: PermitDepartment): void {
        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            width: '400px',
            data: {
                dialog: {
                    title: this.translate.instant('permit.config.departments.archive_dialog_title'),
                    body: this.translate.instant('permit.config.departments.archive_dialog_body'),
                    confirmLabel: this.translate.instant('permit.config.departments.archive'),
                    cancelLabel: this.translate.instant('permit.config.common.cancel'),
                    confirmButtonClass: 'danger-btn',
                },
                component: { data: {}, entry: null },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((confirmed) => {
                if (!confirmed) return;
                this.departmentService
                    .archiveDepartment(department._id)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => this.loadDepartments(),
                        error: (err) => this.messageService.error(this.translate.instant(permitDepartmentErrorKey(err))),
                    });
            });
    }

    onUnarchive(department: PermitDepartment): void {
        this.departmentService
            .unarchiveDepartment(department._id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => this.loadDepartments(),
                error: (err) => this.messageService.error(this.translate.instant(permitDepartmentErrorKey(err))),
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
